import React, { useMemo, useState } from 'react';

import { ROW_HEIGHT } from '../../constants';
import { calculateBlockLayout, getBlockStyle } from '../../helpers';
import styles from '../../index.module.scss';
import type { ProgramBlock, ScheduleType } from '../../types';

type ProgramBlockItemProps = {
  block: ProgramBlock;
  allColumnBlocks: ProgramBlock[];
  selectedBlockId: string | null;
  showInstructor: boolean;
  scheduleType?: ScheduleType;
  enableDrag?: boolean;
  onBlockClick: (
    event: React.MouseEvent<HTMLDivElement>,
    blockId: string
  ) => void;
  onDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    block: ProgramBlock
  ) => void;
};

export const ProgramBlockItem: React.FC<ProgramBlockItemProps> = ({
  block,
  allColumnBlocks,
  selectedBlockId,
  showInstructor,
  scheduleType,
  enableDrag = false,
  onBlockClick,
  onDragStart,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const { width, left, zIndex } = useMemo(() => {
    return calculateBlockLayout(block, allColumnBlocks, scheduleType);
  }, [allColumnBlocks, block, scheduleType]);
  const { top, height } = getBlockStyle(block, ROW_HEIGHT);
  const isSelected = selectedBlockId === block.id;

  const heightValue = parseFloat(height.replace('rem', ''));
  const isShortBlock = heightValue <= 2;

  const finalZIndex = isSelected ? 1000 : zIndex;

  const blockClassNames = [
    styles.block,
    isShortBlock ? styles.shortBlock : styles.normalBlock,
    isSelected ? styles.selected : '',
    !enableDrag ? styles.noDrag : '',
  ]
    .filter(Boolean)
    .join(' ');

  const venueText = block.venue === '其他' ? block.address : block.venue;

  const shouldShowVenueText = showInstructor
    ? heightValue >= 4.25
    : heightValue >= 3.5;

  return (
    <div
      className={`${blockClassNames} ${isDragging ? styles.dragging : ''}`}
      style={{
        top,
        height,
        width,
        left,
        zIndex: finalZIndex,
        position: 'absolute',
        backgroundColor: block.backgroundColor,
      }}
      draggable={enableDrag}
      onClick={(e) => onBlockClick(e, block.id)}
      onDragStart={(e) => {
        if (!enableDrag) {
          e.preventDefault();
          return;
        }
        setIsDragging(true);
        onDragStart(e, block);
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
      }}
      onDragEnd={() => setIsDragging(false)}
    >
      <div className={styles.blockContent}>
        {isShortBlock ? (
          <>
            <div className={styles.blockTitle}>{block.label}</div>
            {showInstructor && block.instructor && (
              <div className={styles.blockInstructor}>{block.instructor}</div>
            )}
            <div className={styles.blockTime}>
              {block.start} - {block.end}
            </div>
            {block.venue && <div>{venueText}</div>}
          </>
        ) : (
          <>
            <div className={styles.blockHeader}>
              <div className={styles.blockTitle}>{block.label}</div>
              {showInstructor && block.instructor && (
                <div className={styles.blockInstructor}>{block.instructor}</div>
              )}
            </div>
            <div className={styles.blockFooter}>
              {showInstructor && heightValue < 3.5 ? null : (
                <div className={styles.blockTime}>
                  {block.start} - {block.end}
                </div>
              )}
              {block.venue && shouldShowVenueText && (
                <div className={styles.blockVenue}>{venueText}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
